// Comparing a draft project's simulation with the current state's, so a suggested change can be judged on what it does
// to the flow of work before it's kept
import { getInsightsForNode, GraphData, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from '../common/linqset-model'
import { formatWorkingTime, RoleCapacity, SimulationReport, SimulationSnapshot } from './simulation-report'

// each role's work and capacity in a workspace, from its organisation model
export const roleCapacitiesFrom = (organisationModel: GraphData | undefined, workspaceKey: string): Map<string, RoleCapacity> => new Map(
    (organisationModel?.nodes || []).filter(n => n.superType === NODE_SUPERTYPE.ROLE).map(n => {
        const i = getInsightsForNode(n, ORGANISATION_MODEL_KEY, workspaceKey)
        return [n._key, { name: n.name, hours: i?.hours || 0, capacity: i?.modelledCapacity || 0, people: n.properties?.people || i?.numPeople || 0 }]
    }),
)

// what the organisation's people and systems cost a year in a workspace, used or not
const COSTED = [NODE_SUPERTYPE.ROLE, NODE_SUPERTYPE.PEOPLE, NODE_SUPERTYPE.SYSTEM]
export const organisationCost = (organisationModel: GraphData | undefined, workspaceKey: string) => (organisationModel?.nodes || [])
    .filter(n => COSTED.includes(n.superType))
    .reduce((sum, n) => {
        const a = getInsightsForNode(n, ORGANISATION_MODEL_KEY, workspaceKey)?.costAllocation
        return sum + (a ? (a.allocated || 0) + (a.unallocated || 0) + (a.unmodelled || 0) : 0)
    }, 0)

// how a scenario ran
export interface ScenarioMetrics {
    produced: number            // outputs produced
    expected: number            // outputs the model expects in the time
    waiting: number             // items waiting at steps at the end
    delaySeconds: number        // the average time an output's items spent waiting on the way
    behind: number              // steps that can't keep up
    outOfStep: number           // steps waiting for their least input
    cost: number                // the organisation's annual cost of its people and systems
}

export const scenarioMetrics = (snapshot: SimulationSnapshot, report: SimulationReport, cost: number): ScenarioMetrics => {
    const produced = snapshot.outputs.reduce((sum, o) => sum + o.produced, 0)
    const waited = snapshot.outputs.reduce((sum, o) => sum + o.produced*Math.max(0, o.leadSeconds - o.workSeconds), 0)
    return {
        produced,
        expected: report.expected,
        waiting: report.queued,
        delaySeconds: produced ? waited/produced : 0,
        behind: report.bottlenecks.filter(b => b.cause !== 'join').length,
        outOfStep: report.bottlenecks.filter(b => b.cause === 'join').length,
        cost,
    }
}

// a step that held work up in either scenario, as each found it
export interface StepComparison {
    key: string
    name: string
    model: string
    current: { queue: number, waitSeconds: number }
    draft?: { queue: number, waitSeconds: number }      // missing when the draft no longer has the step
    better: boolean
}

export interface ScenarioComparison {
    current: ScenarioMetrics
    draft: ScenarioMetrics
    steps: StepComparison[]
    verdict: 'better' | 'mixed' | 'worse' | 'same'
    summary: string
}

// how long a newly arrived item would wait at a step, at the rate it's been finishing items
const waitAt = (snapshot: SimulationSnapshot, key: string) => {
    const step = snapshot.steps.find(s => s.key === key)
    if (!step) return undefined
    const rate = step.completed/Math.max(1, snapshot.tick - 1)
    return { queue: step.queue, waitSeconds: step.queue === 0 ? 0 : rate > 0 ? step.queue/rate*snapshot.secondsPerTick : Infinity }
}

// a change worth calling out: more than a few percent, and more than a little
const changed = (a: number, b: number, least = 1) => Math.abs(b - a) >= least && Math.abs(b - a) > Math.abs(a)*0.03

const percent = (a: number, b: number) => a ? `${Math.round(Math.abs(b - a)/a*100)}%` : `${Math.round(Math.abs(b - a))}`

export const compareScenarios = (current: { snapshot: SimulationSnapshot, report: SimulationReport, metrics: ScenarioMetrics },
    draft: { snapshot: SimulationSnapshot, report: SimulationReport, metrics: ScenarioMetrics }, formatCost: (value: number) => string): ScenarioComparison => {
    // the steps holding work up in either, those that held it up now first
    const keys = [...new Set([...current.report.bottlenecks, ...draft.report.bottlenecks].filter(b => b.cause !== 'join').map(b => b.key))]
    const named = new Map([...draft.report.bottlenecks, ...current.report.bottlenecks].map(b => [b.key, b]))
    const steps: StepComparison[] = keys.map(key => {
        const before = waitAt(current.snapshot, key) || { queue: 0, waitSeconds: 0 }
        const after = waitAt(draft.snapshot, key)
        const b = named.get(key)!
        return { key, name: b.name, model: b.model, current: before, draft: after, better: !after || after.waitSeconds < before.waitSeconds }
    })

    const c = current.metrics, d = draft.metrics
    const gains: string[] = [], losses: string[] = []
    const note = (better: boolean, text: string) => (better ? gains : losses).push(text)
    if (changed(c.produced, d.produced)) note(d.produced > c.produced, `${percent(c.produced, d.produced)} ${d.produced > c.produced ? 'more' : 'fewer'} outputs`)
    if (changed(c.waiting, d.waiting)) note(d.waiting < c.waiting, `${percent(c.waiting, d.waiting)} ${d.waiting < c.waiting ? 'less' : 'more'} work waiting`)
    if (changed(c.delaySeconds, d.delaySeconds, 600)) note(d.delaySeconds < c.delaySeconds, `outputs ${d.delaySeconds < c.delaySeconds ? 'wait' : 'are held up'} ${formatWorkingTime(Math.abs(c.delaySeconds - d.delaySeconds))} ${d.delaySeconds < c.delaySeconds ? 'less' : 'longer'} on the way`)
    if (d.behind !== c.behind) note(d.behind < c.behind, `${Math.abs(c.behind - d.behind)} ${d.behind < c.behind ? 'fewer' : 'more'} steps that can't keep up`)
    // any cost worth mentioning is reported: as a share of the whole organisation's, even a large one looks small
    if (Math.abs(d.cost - c.cost) >= 1000) note(d.cost < c.cost, `costs ${formatCost(Math.abs(d.cost - c.cost))} a year ${d.cost < c.cost ? 'less' : 'more'}`)

    const verdict = !gains.length && !losses.length ? 'same' : !losses.length ? 'better' : !gains.length ? 'worse' : 'mixed'
    const summary = verdict === 'same'
        ? 'The changes make little difference to how work flows.'
        : [gains.length ? `Better: ${gains.join(', ')}.` : '', losses.length ? `${verdict === 'worse' ? 'Worse' : 'But'}: ${losses.join(', ')}.` : ''].filter(s => s).join(' ')
    return { current: c, draft: d, steps, verdict, summary }
}
